/**
 * Approvals + Client Review — Learn-Mode inline tips.
 *
 * Covers the internal approvals workspace (`routes/social/Approvals.tsx` and
 * `components/approvals/*`) and the public, no-login client review page
 * (`routes/public/ClientReview.tsx`). The client-review keys are authored
 * for coverage even though that page is a separate, unauthenticated
 * audience — see the manager-integration note in the sweep report about
 * whether the floating help overlay should mount there at all.
 */

import type { LearnTip } from "./types";

export const approvalsTips: LearnTip[] = [
  {
    key: "social-approvals-thread",
    title: "Review Thread",
    body: "A running log of every approval, change request, and comment left on this post — both from your team and, once shared, from the client.",
  },
  {
    key: "social-approvals-bulk",
    title: "Bulk Actions",
    body: "Check the posts you want to act on, then approve, request changes, or share them for client review all at once instead of one at a time.",
  },
  {
    key: "social-approvals-share",
    title: "Share for Client Review",
    body: "Generates a no-login link so the client can approve or comment on the selected posts themselves. Only posts from a single client can be shared together.",
  },
  {
    key: "social-approvals-attach-design",
    title: "Attach Design Images",
    body: "Renders each selected draft's design and attaches the finished pages as the post's images — a shortcut for when a post was built in the design editor but never attached to its images.",
  },
  {
    key: "social-approvals-workflow",
    title: "Approval Settings",
    body: "Opens your approval rules: whether posts need approval before they publish, who reviews them by default, whether the client reviews them too, whether authors hear about the decision, and who may approve their own posts.",
  },
  {
    key: "social-approvals-filters",
    title: "Sort & Filter the Queue",
    body: "'Due soonest' puts the posts that go out first at the top. Narrow the list to one network, to posts assigned to you, or to drafts Content Autopilot wrote.",
  },
  {
    key: "social-approvals-format-filter",
    title: "Feed or Stories",
    body: "Review feed posts and Reels separately from Stories. The count on each button is how many are waiting. Stories have no caption, so you are approving the picture or video and where it posts.",
  },
  {
    key: "social-approvals-story-note",
    title: "Story Posts",
    body: "This post publishes as a Story, so there is no caption to review. Check the picture or video in the Story-shaped preview and which networks it goes to.",
  },
  {
    key: "social-approvals-story-to-post",
    title: "Stories To Post",
    body: "Stories with interactive stickers (polls, quizzes, links) can't be posted automatically. A person posts each one from the Plerity Social app on their phone and adds the stickers by hand. This list shows every Story still waiting, overdue first. Skip one here, with a reason, only if it won't be posted.",
  },
  {
    key: "social-approvals-story-assist",
    title: "Assisted Story",
    body: "This Story needs a person to post it with real stickers. The list shows each sticker to add: its type, wording and options. A quiz also shows the right answer to mark. Approve the picture and the sticker plan together.",
  },
  {
    key: "social-approvals-captions",
    title: "Network Previews",
    body: "Facebook on the left, Instagram on the right: each shows exactly what that network will get, posted as the client's real account, with its caption, hashtags, first comment and every photo. Swipe the Instagram photos like you would in the app. On a phone, swipe between the two or tap Facebook / Instagram.",
  },
  {
    key: "social-approvals-due",
    title: "Due Time",
    body: "This post is already scheduled. It won't go out until it's approved, so review it before this time.",
  },
  {
    key: "social-approvals-planned",
    title: "Planned Time",
    body: "Content Autopilot picked this time slot. 'Approve & schedule' approves the post and queues it for this time on the planned networks.",
  },
  {
    key: "social-approvals-media-needed",
    title: "Media Needed",
    body: "This draft has its copy but no photo or video yet. You can approve the wording now, but Instagram won't publish a post without media, so attach it in the editor before the proposed time.",
  },
  {
    key: "social-approvals-waiting-on-client",
    title: "Waiting on Client",
    body: "This post is on an active client review link with no decision yet, and its scheduled slot is coming up. The team gets a reminder; the client isn't messaged automatically.",
  },
  {
    key: "social-approvals-approve-schedule",
    title: "Approve & Schedule",
    body: "Approves the draft and schedules it for its planned time, on the client's connected accounts, with each network's own caption. If the time is under 5 minutes away or a network isn't connected, nothing is scheduled and you'll be told why.",
  },
  {
    key: "social-approvals-open-edit",
    title: "Open or Edit the Post",
    body: "Open shows the full post with its per-network status. Edit opens it in the composer so you can fix it yourself instead of sending it back.",
  },
  {
    key: "social-approvals-assign",
    title: "Assign a Reviewer",
    body: "Pick a teammate to review this post. It shows up for them under 'Assigned to me', and their phone is alerted if they use the mobile app.",
  },
  {
    key: "social-settings-approvals",
    title: "Approval Workflow",
    body: "The same approval rules as the gear on the Approvals page: whether posts need approval, the default reviewer, the client review step, decision notifications, and who may approve their own posts.",
  },
  {
    key: "social-approval-workflow-settings",
    title: "Approval Workflow",
    body: "These settings decide how a post moves from draft to published — whether it needs internal approval, a specific reviewer, and whether the client gets a say before it goes out. Apply them organization-wide or just for one client.",
  },
  {
    key: "social-approvals-review-links",
    title: "Client Review Links",
    body: "Active links you've shared with clients for self-serve approval. Each one shows how many posts it covers and lets you copy or revoke it.",
  },
  {
    key: "social-approvals-share-link",
    title: "Client Review Link Created",
    body: "The client can approve, request changes, or comment on each shared item without logging in. Their decisions show up in each post's review thread, and an approval lets the post publish at its scheduled time.",
  },
  {
    key: "social-client-review-item",
    title: "Post Awaiting Your Review",
    body: "This is one of the posts your agency is asking you to review. Read it over, then approve it, request changes, or leave a comment below.",
  },
  {
    key: "social-client-review",
    title: "Your Posts to Review",
    body: "Your agency shared this page so you can approve upcoming social posts, or ask for changes, without needing to log in to anything.",
  },
  {
    key: "social-client-review-name",
    title: "Who's Reviewing",
    body: "Your name is shown to the agency's team next to any approvals or comments you leave, so they know who to follow up with.",
  },
  {
    key: "social-client-review-unreleased",
    title: "Not Open for Decisions Yet",
    body: "You can preview everything on this page now. Approving, requesting changes, and commenting switch on once your agency releases the link. Nothing you look at here is approved until then.",
    bullets: ["Refresh this page after your agency lets you know it's released."],
  },
  {
    key: "social-client-review-done",
    title: "All Done",
    body: "You've reviewed every post on this link. Your decisions are saved for the team automatically — it's safe to close this page.",
  },
  {
    key: "social-approvals-video-qa",
    title: "Video Quality Checks",
    body: "A summary of the automatic checks this video passed before it reached you (for example caption timing, length and format for the platform). Open it to see each check.",
    bullets: ["A failed or warning check is worth a look before you approve, but it never blocks you from deciding."],
  },
  {
    key: "social-approvals-missing-media",
    title: "Missing Media",
    body: "Shows only the posts on this page that have no photo or video yet, so you can work through them without scrolling past everything else.",
  },
  {
    key: "social-approvals-edited-after-approval-banner",
    title: "Edited After Approval",
    body: "These posts were approved, then someone changed the caption, media or details. Any change after approval sends a post back here, and it won't publish at its planned time until it is approved again. The list shows each post's planned time so you can see which ones are close.",
    bullets: ["'Show only these' filters the queue down to just the posts that lost their approval."],
  },
  {
    key: "social-approvals-edited-after-approval",
    title: "Edited After Approval",
    body: "This post was approved and then changed, so it needs approving again before it publishes. Hover the label to see who approved it and who changed it.",
  },
  {
    key: "social-approvals-content-validation",
    title: "Fact Check",
    body: "Shows the result of a fact check on this post's caption and media. Validated means the facts were checked and nothing is outstanding. Fix needed and Hold mean the post should be corrected or held back before it goes out; Needs client confirm means one detail should be confirmed with the client first. Hover the label to see when it was checked and by whom; open the post to see each check.",
    bullets: ["The label disappears if the caption or media change afterwards, because the check no longer applies."],
  },
  {
    key: "social-approvals-add-media",
    title: "Add Media",
    body: "Picks from media your agency already holds and has never posted before — the content library and the media library — so you don't have to shoot or upload something new. 'Replace' swaps the post's media; 'Add' keeps what's there and adds this too.",
    bullets: [
      "A flagged 'Built from older posts' candidate reuses footage from something already published — look closely before using it.",
    ],
  },
  {
    key: "social-approvals-media-provenance",
    title: "Where This Media Came From",
    body: "Shows whether the attached photo or video came from the content library, the media library, or a new design — and flags media built from something already posted, so you can judge it before approving.",
  },
  {
    key: "approvals-audio-provenance",
    title: "Where the Music Came From",
    body: "Where this post's music comes from — Epidemic Sound library, Instagram audio added at publish, AI-generated, or original sound. 'Not recorded' means the builder didn't log it; check before approving.",
  },
  {
    key: "social-provider-media-ip-risk",
    title: "Licensed Character / Celebrity Media",
    body: "This post's video or design uses a GIPHY GIF or sticker that shows a studio character or a real celebrity. Studio/celebrity content — avoid in client posts. It's a warning, not a block: you can still approve, but consider asking for an unflagged sticker.",
  },
];
